import React from "react";
import { View, TouchableOpacity, StyleSheet } from "react-native";
import FastImage from "react-native-fast-image";
import Text from "@components/CustomText";
import { tokens } from "../styles";
import StatusBadge from "./StatusBadge";
import { formatPackingTripLabel } from "../utils";

// One item row inside a CrateSectionCard. Shows image, qty, customer,
// SKU/order id, and a Mark / REMOVE MARKING button. Background flips to
// faint red when the item is packed.

const ISSUE_LABEL = {
  item_damaged: "Damaged",
  wrong_item_received: "Wrong",
  not_found: "Missing",
  partial_not_found: "Partial",
  ofd_db_lost_damage: "DB Loss",
  cx_rto_predelivery: "CX RTO",
  found: "Found",
};

const ItemRow = ({ item, onPressMark, onPressImage, processing }) => {
  const isPacked = Boolean(item?.packed_at);
  const isIssue = Boolean(item?.is_issue);
  const reasonLabel = item?.issue_reason
    ? ISSUE_LABEL[item.issue_reason] || item.issue_reason
    : null;

  return (
    <View
      style={[
        styles.row,
        {
          backgroundColor: isPacked ? tokens.pill.unpacked.bg : tokens.surface,
        },
      ]}
    >
      {item?.product_image ? (
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => onPressImage?.(item.product_image)}
        >
          <FastImage
            source={{ uri: item.product_image }}
            style={styles.thumb}
            resizeMode={FastImage.resizeMode.cover}
          />
        </TouchableOpacity>
      ) : (
        <View style={[styles.thumb, styles.thumbPlaceholder]} />
      )}
      <View style={styles.body}>
        <Text variant="caption_2" style={styles.name} numberOfLines={2}>
          {item?.short_name || "—"}
        </Text>
        <Text variant="caption_2" style={styles.meta} numberOfLines={1}>
          {item?.user_name || "—"} · Qty {item?.quantity ?? 0}
          {item?.order_item_id ? ` · #${item.order_item_id}` : ""}
        </Text>
        {reasonLabel ? (
          <Text variant="caption_2" style={styles.reason}>
            {reasonLabel}
          </Text>
        ) : null}
        {item?.is_ofd ? (
          <View style={styles.tripBadgeWrap}>
            <StatusBadge
              label={formatPackingTripLabel(item?.trip_id)}
              state="ofd"
            />
          </View>
        ) : null}
      </View>
      <TouchableOpacity
        activeOpacity={0.85}
        disabled={isPacked || processing}
        onPress={() => onPressMark?.(item)}
        style={[
          styles.markBtn,
          isIssue ? styles.markBtnDanger : styles.markBtnDefault,
          (isPacked || processing) && styles.markBtnDisabled,
        ]}
      >
        <Text
          variant="caption_2"
          style={isIssue ? styles.markBtnDangerText : styles.markBtnText}
        >
          {isIssue ? "REMOVE" : "NO ISSUE"}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

export default ItemRow;

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  thumb: {
    width: 40,
    height: 40,
    borderRadius: tokens.radius.sm,
    marginRight: tokens.spacing.medium,
  },
  thumbPlaceholder: { backgroundColor: tokens.borderLight },
  body: { flex: 1 },
  name: { color: tokens.textPrimary, fontSize: 12, fontWeight: "600" },
  meta: { color: tokens.textSecondary, fontSize: 11, marginTop: 2 },
  reason: {
    color: tokens.error,
    fontSize: 11,
    marginTop: 2,
    fontWeight: "600",
  },
  tripBadgeWrap: { marginTop: 4, alignSelf: "flex-start" },
  markBtn: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    marginLeft: tokens.spacing.small,
  },
  markBtnDefault: {
    borderColor: tokens.borderMedium,
    backgroundColor: tokens.surface,
  },
  markBtnDanger: {
    borderColor: tokens.error,
    backgroundColor: tokens.error,
  },
  markBtnDisabled: { opacity: 0.4 },
  markBtnText: { color: tokens.textPrimary, fontSize: 11, fontWeight: "700" },
  markBtnDangerText: { color: tokens.surface, fontSize: 11, fontWeight: "700" },
});
